import { useEffect, useState } from 'react';
import {
  SlotGrid, StandingPick, WeeklyTime, WEEKDAYS, WEEK_ORDER, dayMonth, time12, slotText, CLASH_REASON,
} from './standingApi';

/* Admin weekly-slots picker — the desk sale's "weekly times" step and the
   member modal's add / move. Same grid as the member checkout: times free on
   every week of the validity, clashing times marked with their dates. Staff
   (unlike members) may take a clashing time on purpose: those dates are then
   skipped, visibly, never double-booked. */

export default function WeeklySlotPicker({ title, subtitle, load, count, initial, saving, error, onClose, onSave }: {
  title: string;
  subtitle?: string;
  /** Fetch the grid (for a branch, when one is chosen). */
  load: (branchId: string | null) => Promise<SlotGrid>;
  /** Override how many times (moving one slot = 1). */
  count?: { min: number; max: number };
  initial?: WeeklyTime[];
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (pick: StandingPick) => void;
}) {
  const [branchId, setBranchId] = useState<string | null>(null);
  const [grid, setGrid] = useState<SlotGrid | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<WeeklyTime[]>(initial ?? []);
  const [acceptClashes, setAcceptClashes] = useState(false);

  useEffect(() => {
    let live = true;
    setGrid(null);
    setLoadError(null);
    load(branchId)
      .then(g => {
        if (!live) return;
        setGrid(g);
        // A renewal / first set starts from the member's current times; a move starts empty.
        if (!count && !initial?.length && g.current.length) setPicked(g.current.slice(0, (count ?? g.slots).max));
      })
      .catch(e => live && setLoadError(e.message || 'Could not load times'));
    return () => { live = false; };
  }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const need = count ?? grid?.slots ?? { min: 1, max: 1 };
  const isPicked = (t: WeeklyTime) => picked.some(p => p.weekday === t.weekday && p.startTime === t.startTime);
  const toggle = (t: WeeklyTime) => {
    if (isPicked(t)) return setPicked(p => p.filter(x => !(x.weekday === t.weekday && x.startTime === t.startTime)));
    setPicked(p => [...(p.length >= need.max ? p.slice(1) : p), t]);
  };
  const pickedClashes = grid
    ? picked.flatMap(p => grid.days.find(d => d.weekday === p.weekday)?.times.find(t => t.startTime === p.startTime)?.clashes ?? [])
    : [];
  const ready = picked.length >= need.min && picked.length <= need.max && (pickedClashes.length === 0 || acceptClashes);
  const maxTimes = grid ? Math.max(0, ...grid.days.map(d => d.times.length)) : 0;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-slate-800 truncate">{title}</h3>
            <p className="text-xs text-slate-500 truncate">
              {subtitle ?? (grid ? `${grid.trainer.name} · ${grid.bookable.name} · ${grid.durationMin} min · ${grid.window.capped ? `from ${dayMonth(grid.window.from)}` : `${grid.window.weeks} weeks from ${dayMonth(grid.window.from)}`}` : ' ')}
            </p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-500 font-bold" aria-label="Close">✕</button>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1 space-y-3">
          {loadError && <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{loadError}</div>}
          {!grid && !loadError && <div className="h-64 rounded-xl bg-slate-100 animate-pulse" />}
          {grid && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {grid.branches.length > 1 && grid.branches.map(b => (
                  <button key={b.id} onClick={() => { setBranchId(b.id); setPicked([]); }}
                    className={`px-3 h-8 rounded-full text-xs font-bold border ${b.id === grid.branchId ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>{b.name}</button>
                ))}
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 ml-auto">
                  {need.min === need.max ? `Choose ${need.min}` : `Choose ${need.min}–${need.max}`} · {picked.length} chosen
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-separate" style={{ borderSpacing: 6 }}>
                  <thead>
                    <tr>
                      {WEEK_ORDER.map(w => {
                        const day = grid.days.find(d => d.weekday === w);
                        return (
                          <th key={w} className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">
                            {WEEKDAYS[w]}
                            <div className="text-[10px] font-semibold normal-case tracking-normal text-slate-400">{day ? `${day.times.filter(t => !t.clashes.length).length} free` : '—'}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: maxTimes }).map((_, row) => (
                      <tr key={row}>
                        {WEEK_ORDER.map(w => {
                          const t = grid.days.find(d => d.weekday === w)?.times[row];
                          if (!t) return <td key={w} />;
                          const slot = { weekday: w, startTime: t.startTime };
                          const on = isPicked(slot);
                          const clash = t.clashes.length > 0;
                          const tip = clash ? t.clashes.map(c => `${dayMonth(c.date)} — ${CLASH_REASON[c.reason]}`).join('\n') : 'Free every week';
                          return (
                            <td key={w}>
                              <button title={tip} onClick={() => toggle(slot)}
                                className={`w-full min-h-[44px] rounded-lg border text-xs font-bold px-1 py-1 transition-transform active:scale-95 ${on ? 'bg-lime-400 border-lime-500 text-slate-900' : clash ? 'bg-slate-50 border-dashed border-slate-300 text-slate-400' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}>
                                {time12(t.startTime)}
                                {clash && <div className="text-[9px] font-semibold text-rose-500 truncate">{t.clashes.slice(0, 2).map(c => dayMonth(c.date)).join(', ')}{t.clashes.length > 2 ? ` +${t.clashes.length - 2}` : ''}</div>}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {maxTimes === 0 && <p className="text-sm text-slate-500 p-4">{grid.trainer.name} has no time free on any week of this period. Check their hours in Team → Availability.</p>}
              </div>

              {pickedClashes.length > 0 && (
                <label className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-xs">
                  <input type="checkbox" checked={acceptClashes} onChange={e => setAcceptClashes(e.target.checked)} className="mt-0.5" />
                  <span>
                    The chosen times clash on {pickedClashes.length} date{pickedClashes.length === 1 ? '' : 's'} ({pickedClashes.slice(0, 5).map(c => dayMonth(c.date)).join(', ')}{pickedClashes.length > 5 ? '…' : ''}).
                    Keep them anyway — those dates are skipped and shown to the member, nobody is double-booked.
                  </span>
                </label>
              )}
            </>
          )}
          {error && <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3">
          <div className="text-sm text-slate-600 truncate flex-1">{picked.length ? `Every ${picked.map(slotText).join(' · ')}` : 'No times chosen'}</div>
          <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button disabled={!ready || !grid || saving} onClick={() => grid && onSave({ times: picked, branchId: grid.branchId, ...(acceptClashes && pickedClashes.length ? { acceptClashes: true } : {}) })}
            className="px-5 h-10 rounded-lg text-sm font-black bg-indigo-600 text-white disabled:opacity-40">
            {saving ? 'Saving…' : 'Hold these times'}
          </button>
        </div>
      </div>
    </div>
  );
}
